import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import type { PortalUserProfileDto } from '@xcentral/api-client';
import { useApi } from '../hooks/useApi';
import { BRANDS, DEFAULT_BRAND, normalizeBrand, type Brand, type BrandAssets } from './brands';

/**
 * BT001 #12382 (S4 — Client-portal brand theming, BR-ENT-006).
 *
 * Holds the current {@link Brand} and reflects it onto
 * `document.documentElement.dataset.brand` so the CSS-variable palette
 * switch in src/styles/globals.css takes effect at runtime — one app,
 * runtime theme, no rebuild.
 *
 * - Defaults to CONSCIERRA.
 * - When authenticated, fetches GET /api/v1/portal-users/me and sets the
 *   brand from the response (falling back to CONSCIERRA if missing).
 * - Exposes a setter so the onboarding-accept page (AC3 / Bug #11925) can
 *   push a brand it learned from the accept-response before it has an
 *   authenticated session.
 * - SSG-safe: guards all `document` access and skips the fetch when there
 *   is no MSAL/window context (mirrors RootLayout's `getMsalInstance()`
 *   null-guard pattern).
 */

interface BrandContextValue {
  readonly brand: Brand;
  readonly assets: BrandAssets;
  readonly setBrand: (brand: Brand | string | null | undefined) => void;
}

const BrandContext = createContext<BrandContextValue | null>(null);

// Stable no-op reference for useBrand()'s outside-provider fallback — a fresh
// arrow function on every call would break dependency arrays at call sites.
const NOOP_SET_BRAND: BrandContextValue['setBrand'] = () => {};

function applyBrandToDocument(brand: Brand): void {
  if (typeof document === 'undefined') return;
  if (brand === 'XCENTRAL') {
    document.documentElement.dataset.brand = 'xcentral';
  } else {
    delete document.documentElement.dataset.brand;
  }
}

/**
 * Fetches the caller's brand from /api/v1/portal-users/me once authenticated.
 * Split out so it only runs (and only calls useApi/MSAL hooks) inside a tree
 * that actually has MSAL context — BrandProviderInner is never mounted during
 * SSG / the no-MSAL branch of RootLayout.
 */
function useAuthenticatedBrandFetch(setBrand: (brand: Brand) => void): void {
  const isAuthenticated = useIsAuthenticated();
  const api = useApi();

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;

    (async () => {
      try {
        const profile = await api.get<PortalUserProfileDto>('/api/v1/portal-users/me');
        if (cancelled) return;
        setBrand(normalizeBrand(profile.brand));
      } catch {
        // Network/auth failure — keep the current (default) brand rather than
        // block rendering. The dashboard/pages themselves surface API errors.
        if (!cancelled) setBrand(DEFAULT_BRAND);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- api is a stable useMemo'd object; re-running per isAuthenticated is correct.
  }, [isAuthenticated]);
}

function BrandProviderBase({ children, withAuthFetch }: { children: ReactNode; withAuthFetch: boolean }) {
  const [brand, setBrandState] = useState<Brand>(DEFAULT_BRAND);

  const setBrand = useCallback((next: Brand | string | null | undefined) => {
    setBrandState(normalizeBrand(next));
  }, []);

  useEffect(() => {
    applyBrandToDocument(brand);
  }, [brand]);

  // eslint-disable-next-line react-hooks/rules-of-hooks -- withAuthFetch is fixed per call site (never toggles), so hook-call order is stable.
  if (withAuthFetch) useAuthenticatedBrandFetch(setBrand);

  const value = useMemo<BrandContextValue>(() => ({
    brand,
    assets: BRANDS[brand],
    setBrand,
  }), [brand, setBrand]);

  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
}

/**
 * Use inside a tree that has MSAL context (i.e. wrapped by <MsalProvider>).
 * Fetches the authenticated user's brand automatically.
 */
export function BrandProvider({ children }: { children: ReactNode }) {
  return <BrandProviderBase withAuthFetch>{children}</BrandProviderBase>;
}

/**
 * Use for the SSG / pre-MSAL-init render path (no MSAL context available).
 * Brand defaults to CONSCIERRA and is never fetched — matches RootLayout's
 * `!instance` branch.
 */
export function StaticBrandProvider({ children }: { children: ReactNode }) {
  return <BrandProviderBase withAuthFetch={false}>{children}</BrandProviderBase>;
}

export function useBrand(): BrandContextValue {
  const ctx = useContext(BrandContext);
  if (!ctx) {
    // Graceful default rather than throwing — pages rendered outside the
    // provider (defensive coding per the BT001 #12382 brief) still get a
    // usable Conscierra-default brand rather than a crash.
    return { brand: DEFAULT_BRAND, assets: BRANDS[DEFAULT_BRAND], setBrand: NOOP_SET_BRAND };
  }
  return ctx;
}
